import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Link2, AlertTriangle, Phone, Search, ChevronLeft, ChevronRight, X, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { safeStr } from '../utils/safeStr';
import { brandChipBrand, brandIconAccent } from './brandChrome';

// ─── Types ───────────────────────────────────────────────────────────

interface Sender {
    sender_id: number | null;
    sender_name: string;
    sender_username: string | null;
    message_count: number;
    channel_count: number;
    unique_upi_count: number;
    unique_wallet_count: number;
    repeated_upi_count: number;
    repeated_wallet_count: number;
    avg_messages_per_day: number;
    risk_score: number;
    risk_flags: string[];
    first_seen: string | null;
    last_seen: string | null;
}

interface EntityLink {
    channel: string;
    message_id: number;
    sender_id: number | null;
    sender_username: string | null;
    upi_id: string;
    wallet_address: string;
    link_type: string;
    confidence: number;
    source: string;
    first_seen: string | null;
    last_seen: string | null;
}

interface CrossHit {
    hit_type: string;
    sender_id: number | null;
    channel: string;
    upi_id: string | null;
    wallet_address: string | null;
    merchant_advertiser_id: string | null;
    entity_source: string | null;
    entity_name: string | null;
    entity_category: string | null;
    entity_risk_level: string | null;
    confidence: number;
    details: Record<string, unknown>;
    first_seen: string | null;
    last_seen: string | null;
}

interface Artifact {
    channel: string;
    message_id: number;
    sender_id: number | null;
    sender_username: string | null;
    phones: string[];
    emails: string[];
    ifsc_codes: string[];
    bank_mentions: string[];
    whatsapp_handles: string[];
    extracted_at: string | null;
}

interface ArtifactSummary {
    total_phones: number;
    total_emails: number;
    total_ifsc: number;
    total_banks: number;
    total_whatsapp: number;
}

type Tab = 'senders' | 'links' | 'cross-hits' | 'artifacts';

const PAGE_SIZE = 20;

const FLAG_LABELS: Record<string, string> = {
    high_velocity: 'High Velocity',
    multi_channel_spread: 'Multi-Channel',
    repeated_upi_reuse: 'UPI Reuse',
    repeated_wallet_reuse: 'Wallet Reuse',
};

const truncAddr = (addr: string) =>
    addr.length > 16 ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : addr;

const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString() : '-';

const inferChain = (addr: string) =>
    addr.startsWith('T') ? 'TRX' : addr.startsWith('0x') ? 'ETH' : 'BTC';

const explorerUrl = (addr: string) => {
    if (addr.startsWith('T')) return `https://tronscan.org/#/address/${addr}`;
    if (addr.startsWith('0x')) return `https://etherscan.io/address/${addr}`;
    return `https://www.blockchain.com/btc/address/${addr}`;
};

/** Clickable wallet badge with explorer link + investigate action */
function WalletBadge({ address, navigate }: { address: string; navigate: (path: string) => void }) {
    const chain = inferChain(address);
    return (
        <span className="inline-flex items-center gap-1">
            <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${brandChipBrand}`}>
                {chain}: {truncAddr(address)}
            </span>
            <a
                href={explorerUrl(address)}
                target="_blank"
                rel="noopener noreferrer"
                className={`${brandIconAccent} hover:text-primary/80`}
                title="View on blockchain explorer"
            >
                <ExternalLink className="w-3 h-3" />
            </a>
            <button
                onClick={() => navigate(`/workbench?address=${address}&chain=${chain}`)}
                className="px-1.5 py-0.5 text-[9px] bg-blue-600 text-foreground rounded hover:bg-blue-700"
                title="Investigate in Workbench"
            >
                Investigate
            </button>
        </span>
    );
}

// ─── Component ───────────────────────────────────────────────────────

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function TelegramIntelPanel({ isOpen, onClose }: Props) {
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>('senders');
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);

    // Senders
    const [senders, setSenders] = useState<Sender[]>([]);
    const [sendersTotal, setSendersTotal] = useState(0);

    // Entity links
    const [links, setLinks] = useState<EntityLink[]>([]);
    const [linksTotal, setLinksTotal] = useState(0);

    // Cross-source hits
    const [hits, setHits] = useState<CrossHit[]>([]);
    const [hitsTotal, setHitsTotal] = useState(0);
    const [hitFilter, setHitFilter] = useState<string>('');

    // Artifacts
    const [artifacts, setArtifacts] = useState<Artifact[]>([]);
    const [artifactsTotal, setArtifactsTotal] = useState(0);
    const [artifactSummary, setArtifactSummary] = useState<ArtifactSummary | null>(null);

    const [loading, setLoading] = useState(false);

    // Channel filter
    const [channels, setChannels] = useState<{ name: string; enabled: boolean }[]>([]);
    const [channelFilter, setChannelFilter] = useState<string>('');

    // Load channels list on mount
    useEffect(() => {
        api.get('/scrapers/telegram/channels').then(({ data }) => {
            setChannels(data.channels || []);
        }).catch(() => {});
    }, []);

    // Reset pagination on tab/search/filter change
    useEffect(() => { setOffset(0); }, [tab, search, hitFilter, channelFilter]);

    const fetchData = useCallback(async () => {
        if (!isOpen) return;
        setLoading(true);
        try {
            const chParam = channelFilter ? { channel: channelFilter } : {};
            if (tab === 'senders') {
                const { data } = await api.get('/scrapers/telegram/senders', {
                    params: { limit: PAGE_SIZE, offset },
                });
                setSenders(data.senders || []);
                setSendersTotal(data.total || 0);
            } else if (tab === 'links') {
                const { data } = await api.get('/scrapers/telegram/entity-links', {
                    params: { limit: PAGE_SIZE, offset, ...(search ? { search } : {}), ...chParam },
                });
                setLinks(data.links || []);
                setLinksTotal(data.total || 0);
            } else if (tab === 'cross-hits') {
                const { data } = await api.get('/scrapers/telegram/cross-hits', {
                    params: { limit: PAGE_SIZE, offset, ...(hitFilter ? { hit_type: hitFilter } : {}), ...chParam },
                });
                setHits(data.hits || []);
                setHitsTotal(data.total || 0);
            } else if (tab === 'artifacts') {
                const { data } = await api.get('/scrapers/telegram/artifacts', {
                    params: { limit: PAGE_SIZE, offset, ...(search ? { search } : {}), ...chParam },
                });
                setArtifacts(data.artifacts || []);
                setArtifactsTotal(data.total || 0);
                setArtifactSummary(data.summary || null);
            }
        } catch (err) {
            console.error(`Failed to fetch telegram ${tab}:`, err);
        } finally {
            setLoading(false);
        }
    }, [isOpen, tab, offset, search, hitFilter, channelFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (!isOpen) return null;

    const totalForTab = tab === 'senders' ? sendersTotal : tab === 'links' ? linksTotal : tab === 'cross-hits' ? hitsTotal : artifactsTotal;
    const hasNext = offset + PAGE_SIZE < totalForTab;
    const hasPrev = offset > 0;

    const showSearch = tab === 'links' || tab === 'artifacts';

    return (
        <div className="bg-card border border-blue-500/20 rounded-lg p-6 mb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Telegram Intelligence
                    <span className="text-xs font-normal text-muted-foreground ml-1">Derived from scraped data</span>
                </h2>
                <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm px-2">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 border-b border-border pb-2">
                {([
                    ['senders', 'High Risk Senders', Shield],
                    ['links', 'UPI-Wallet Links', Link2],
                    ['cross-hits', 'Cross-Source Alerts', AlertTriangle],
                    ['artifacts', 'Extracted Artifacts', Phone],
                ] as [Tab, string, typeof Shield][]).map(([key, label, Icon]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
                            tab === key
                                ? 'bg-blue-500/10 text-blue-400 border-b-2 border-blue-500'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
            </div>

            {/* Search + filters row */}
            <div className="flex items-center gap-3 mb-4">
                {showSearch && (
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={tab === 'links' ? 'Search UPI or wallet address...' : 'Search phones, emails, IFSC...'}
                            className="w-full pl-7 pr-3 py-1.5 text-xs bg-background border border-input rounded"
                        />
                    </div>
                )}
                {tab === 'cross-hits' && (
                    <select
                        value={hitFilter}
                        onChange={(e) => setHitFilter(e.target.value)}
                        className="px-2 py-1.5 text-xs bg-background border border-input rounded"
                    >
                        <option value="">All hit types</option>
                        <option value="wallet_entity_attribution">Entity Attribution</option>
                        <option value="upi_to_binance_fingerprint">UPI-Binance Fingerprint</option>
                    </select>
                )}
                {tab !== 'senders' && channels.length > 0 && (
                    <select
                        value={channelFilter}
                        onChange={(e) => setChannelFilter(e.target.value)}
                        className="px-2 py-1.5 text-xs bg-background border border-input rounded max-w-[180px]"
                    >
                        <option value="">All channels</option>
                        {channels.map(ch => (
                            <option key={ch.name} value={ch.name}>{ch.name}</option>
                        ))}
                    </select>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto">
                    {totalForTab.toLocaleString()} result{totalForTab !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Loading */}
            {loading && <div className="text-center py-6 text-muted-foreground text-sm">Loading...</div>}

            {/* ── SENDERS TAB ── */}
            {!loading && tab === 'senders' && (
                senders.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-border text-left text-muted-foreground">
                                    <th className="py-2 px-2">Sender</th>
                                    <th className="py-2 px-2 text-center">Msgs</th>
                                    <th className="py-2 px-2 text-center">Ch.</th>
                                    <th className="py-2 px-2 text-center">UPIs</th>
                                    <th className="py-2 px-2 text-center">Wallets</th>
                                    <th className="py-2 px-2 text-center">Msgs/day</th>
                                    <th className="py-2 px-2">Risk</th>
                                    <th className="py-2 px-2">Flags</th>
                                    <th className="py-2 px-2">Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {senders.map((s, i) => {
                                    const riskColor = s.risk_score >= 0.7 ? 'red' : s.risk_score >= 0.4 ? 'orange' : 'green';
                                    return (
                                        <tr key={s.sender_id ?? i} className="border-b border-border/50 hover:bg-secondary/30">
                                            <td className="py-2 px-2">
                                                <div className="font-medium">{s.sender_name || 'Unknown'}</div>
                                                {s.sender_username && (
                                                    <div className="text-[10px] text-muted-foreground">@{s.sender_username}</div>
                                                )}
                                            </td>
                                            <td className="py-2 px-2 text-center font-mono">{s.message_count}</td>
                                            <td className="py-2 px-2 text-center font-mono">{s.channel_count}</td>
                                            <td className="py-2 px-2 text-center font-mono">{s.unique_upi_count}</td>
                                            <td className="py-2 px-2 text-center font-mono">{s.unique_wallet_count}</td>
                                            <td className="py-2 px-2 text-center font-mono">{s.avg_messages_per_day.toFixed(1)}</td>
                                            <td className="py-2 px-2">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${
                                                                riskColor === 'red' ? 'bg-red-500' :
                                                                riskColor === 'orange' ? 'bg-orange-500' : 'bg-green-500'
                                                            }`}
                                                            style={{ width: `${Math.round(s.risk_score * 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className={`font-mono ${
                                                        riskColor === 'red' ? 'text-red-500' :
                                                        riskColor === 'orange' ? 'text-orange-500' : 'text-green-500'
                                                    }`}>
                                                        {(s.risk_score * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-2 px-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {s.risk_flags.map((f) => (
                                                        <span key={f} className={`px-1.5 py-0.5 rounded text-[10px] ${
                                                            f === 'high_velocity' ? 'bg-red-500/10 text-red-400' :
                                                            f === 'multi_channel_spread' ? 'bg-orange-500/10 text-orange-400' :
                                                            f === 'repeated_upi_reuse' ? 'bg-yellow-500/10 text-yellow-400' :
                                                            'bg-primary/10 text-primary'
                                                        }`}>
                                                            {FLAG_LABELS[f] || f}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-2 px-2 text-[10px] text-muted-foreground whitespace-nowrap">
                                                {fmtDate(s.first_seen)} - {fmtDate(s.last_seen)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <EmptyState text="No sender intelligence found. Run the intel feature build script to generate sender profiles." />
                )
            )}

            {/* ── LINKS TAB ── */}
            {!loading && tab === 'links' && (
                links.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-border text-left text-muted-foreground">
                                    <th className="py-2 px-2">UPI ID</th>
                                    <th className="py-2 px-2">Wallet Address</th>
                                    <th className="py-2 px-2">Channel</th>
                                    <th className="py-2 px-2">Sender</th>
                                    <th className="py-2 px-2 text-center">Confidence</th>
                                    <th className="py-2 px-2">Seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {links.map((l, i) => (
                                    <tr key={i} className="border-b border-border/50 hover:bg-secondary/30">
                                        <td className="py-2 px-2">
                                            <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded font-mono text-[10px]">
                                                {l.upi_id}
                                            </span>
                                        </td>
                                        <td className="py-2 px-2">
                                            <WalletBadge address={l.wallet_address} navigate={navigate} />
                                        </td>
                                        <td className="py-2 px-2 text-blue-400">#{l.channel}</td>
                                        <td className="py-2 px-2">{l.sender_username ? `@${l.sender_username}` : l.sender_id || '-'}</td>
                                        <td className="py-2 px-2 text-center">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                l.confidence >= 0.8 ? 'bg-green-500/10 text-green-400' :
                                                l.confidence >= 0.5 ? 'bg-yellow-500/10 text-yellow-400' :
                                                'bg-gray-500/10 text-gray-400'
                                            }`}>
                                                {(l.confidence * 100).toFixed(0)}%
                                            </span>
                                        </td>
                                        <td className="py-2 px-2 text-[10px] text-muted-foreground">{fmtDate(l.last_seen)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <EmptyState text="No UPI-wallet links found. Run the intel feature build script to generate identity links." />
                )
            )}

            {/* ── CROSS-HITS TAB ── */}
            {!loading && tab === 'cross-hits' && (
                hits.length > 0 ? (
                    <div className="space-y-2 max-h-[500px] overflow-auto">
                        {hits.map((h, i) => {
                            const isEntity = h.hit_type === 'wallet_entity_attribution';
                            return (
                                <div
                                    key={i}
                                    className={`p-3 rounded-lg border transition-colors ${
                                        isEntity
                                            ? 'border-red-500/30 bg-red-500/5 hover:bg-red-500/10'
                                            : 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                                isEntity
                                                    ? 'bg-red-500/15 text-red-400'
                                                    : 'bg-amber-500/15 text-amber-400'
                                            }`}>
                                                {isEntity ? 'ENTITY MATCH' : 'UPI FINGERPRINT'}
                                            </span>
                                            <span className={`text-[10px] font-mono ${
                                                h.confidence >= 0.7 ? 'text-red-400' :
                                                h.confidence >= 0.4 ? 'text-orange-400' : 'text-gray-400'
                                            }`}>
                                                {(h.confidence * 100).toFixed(0)}% confidence
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground">{fmtDate(h.last_seen)}</span>
                                    </div>

                                    <div className="flex flex-wrap gap-3 text-xs">
                                        {isEntity && (
                                            <>
                                                {h.wallet_address && (
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-muted-foreground">Wallet: </span>
                                                        <WalletBadge address={h.wallet_address} navigate={navigate} />
                                                    </div>
                                                )}
                                                {safeStr(h.entity_name) && (
                                                    <div>
                                                        <span className="text-muted-foreground">Entity: </span>
                                                        <span className="font-medium text-red-400">{safeStr(h.entity_name)}</span>
                                                    </div>
                                                )}
                                                {h.entity_category && (
                                                    <div>
                                                        <span className="text-muted-foreground">Category: </span>
                                                        <span>{h.entity_category}</span>
                                                    </div>
                                                )}
                                                {h.entity_risk_level && (
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                                        h.entity_risk_level === 'critical' ? 'bg-red-500/15 text-red-400' :
                                                        h.entity_risk_level === 'high' ? 'bg-orange-500/15 text-orange-400' :
                                                        'bg-gray-500/15 text-gray-400'
                                                    }`}>
                                                        {h.entity_risk_level}
                                                    </span>
                                                )}
                                                {h.entity_source && (
                                                    <div>
                                                        <span className="text-muted-foreground">Source: </span>
                                                        <span>{h.entity_source}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {!isEntity && (
                                            <>
                                                {h.upi_id && (
                                                    <div>
                                                        <span className="text-muted-foreground">UPI: </span>
                                                        <span className="font-mono text-green-400">{h.upi_id}</span>
                                                    </div>
                                                )}
                                                {h.merchant_advertiser_id && (
                                                    <div>
                                                        <span className="text-muted-foreground">Merchant: </span>
                                                        <span>{(h.details as Record<string, string>)?.merchant_name || h.merchant_advertiser_id}</span>
                                                    </div>
                                                )}
                                                {(h.details as Record<string, string[]>)?.payment_method_types && (
                                                    <div>
                                                        <span className="text-muted-foreground">Methods: </span>
                                                        <span>{((h.details as Record<string, string[]>).payment_method_types || []).join(', ')}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {h.channel && (
                                            <div>
                                                <span className="text-muted-foreground">Channel: </span>
                                                <span className="text-blue-400">#{h.channel}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <EmptyState text="No cross-source hits found. Run the intel feature build script to generate cross-source matches." />
                )
            )}

            {/* ── ARTIFACTS TAB ── */}
            {!loading && tab === 'artifacts' && (
                <>
                    {/* Summary cards */}
                    {artifactSummary && (
                        <div className="grid grid-cols-5 gap-3 mb-4">
                            {[
                                ['Phones', artifactSummary.total_phones, 'blue'],
                                ['Emails', artifactSummary.total_emails, 'cyan'],
                                ['IFSC Codes', artifactSummary.total_ifsc, 'orange'],
                                ['Banks', artifactSummary.total_banks, 'green'],
                                ['WhatsApp', artifactSummary.total_whatsapp, 'green'],
                            ].map(([label, count, color]) => (
                                <div key={label as string} className="bg-secondary/30 rounded p-3 text-center">
                                    <div className="text-[10px] text-muted-foreground">{label as string}</div>
                                    <div className={`text-lg font-bold text-${color as string}-500`}>{(count as number).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {artifacts.length > 0 ? (
                        <div className="space-y-2 max-h-[400px] overflow-auto">
                            {artifacts.map((a, i) => (
                                <div key={i} className="p-3 border border-border rounded-lg hover:border-blue-500/20 transition-colors">
                                    <div className="flex justify-between text-[10px] text-muted-foreground mb-2">
                                        <span className="text-blue-400">#{a.channel}</span>
                                        <div className="flex items-center gap-3">
                                            {a.sender_username && <span>@{a.sender_username}</span>}
                                            <span>{a.extracted_at ? new Date(a.extracted_at).toLocaleString() : ''}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {a.phones.map((p, j) => (
                                            <span key={`ph-${j}`} className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-[10px] font-mono border border-blue-500/20">
                                                {p}
                                            </span>
                                        ))}
                                        {a.emails.map((e, j) => (
                                            <span key={`em-${j}`} className="px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded text-[10px] font-mono border border-cyan-500/20">
                                                {e}
                                            </span>
                                        ))}
                                        {a.ifsc_codes.map((c, j) => (
                                            <span key={`if-${j}`} className="px-1.5 py-0.5 bg-orange-500/10 text-orange-400 rounded text-[10px] font-mono border border-orange-500/20">
                                                IFSC: {c}
                                            </span>
                                        ))}
                                        {a.bank_mentions.map((b, j) => (
                                            <span key={`bk-${j}`} className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded text-[10px] border border-green-500/20 capitalize">
                                                {b}
                                            </span>
                                        ))}
                                        {a.whatsapp_handles.map((w, j) => (
                                            <span key={`wa-${j}`} className="px-1.5 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400 rounded text-[10px] font-mono border border-green-500/20">
                                                WA: {w}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState text="No artifacts found. Run the intel feature build to extract phones, emails, IFSC codes, bank mentions, and WhatsApp handles." />
                    )}
                </>
            )}

            {/* Pagination */}
            {totalForTab > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                    <span className="text-[10px] text-muted-foreground">
                        Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, totalForTab)} of {totalForTab.toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                        <button
                            disabled={!hasPrev}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            className="px-2 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-30"
                        >
                            <ChevronLeft className="w-3 h-3" />
                        </button>
                        <button
                            disabled={!hasNext}
                            onClick={() => setOffset(offset + PAGE_SIZE)}
                            className="px-2 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-30"
                        >
                            <ChevronRight className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState({ text }: { text: string }) {
    return (
        <div className="text-center py-8 text-muted-foreground text-sm">
            {text}
        </div>
    );
}
